import type { IntegrationDescriptor, IntegrationMode, ProvidersIntegrations } from './types';
import { effectiveMode } from './binding-mode';
import { groupByCategory, integrationKey, unknownEntries } from './integration-state';

/**
 * THE GEOMETRY OF "WHAT IS REAL HERE" — the diagram view of the Integrations
 * section, laid out in code from the inventory. No hand-authored path strings,
 * no chart library, no copy: this module computes coordinates and nothing else,
 * and every word it carries arrives through {@link DiagramLabels}.
 *
 * ── THE ONE RULE THE PICTURE ENCODES ─────────────────────────────────────────
 * The instance has a BOUNDARY, and where a provider is drawn relative to it is
 * not decoration — it is the fact:
 *
 *   · mode `mock`            → drawn INSIDE the boundary, and its flow arrow
 *                              stops short of the edge. That is literally what
 *                              happens: the call is answered in-process and
 *                              never reaches a vendor. An arrow that crossed
 *                              would be a lie.
 *   · `sandbox`/`production` → drawn OUTSIDE, and the arrow CROSSES the edge,
 *                              through a gate marked at the crossing point.
 *   · supported but unwired  → a GHOST outside, with NO ARROW AT ALL. Nothing
 *                              flows, so nothing is drawn flowing.
 *   · unreachable declarant  → one of OUR services, so it sits inside — joined
 *                              by a BROKEN link, because we did not get an
 *                              answer. Never "nothing there"; see the three
 *                              states in `integration-state.ts`.
 *
 * Because inside/outside is derived from `mode` and `enabled` in ONE place
 * ({@link nodePlacement}), the picture cannot drift from the list beside it.
 *
 * ── WHY THE ARROWS ARE STRAIGHT ──────────────────────────────────────────────
 * Every branch leaves a single vertical bus at exactly the row's y, so a link is
 * one horizontal segment. That is what lets the crossing point be exact rather
 * than solved for, keeps the pip's `<animateMotion path>` identical to the
 * arrow's `d` (one string, one source of truth), and makes the whole picture
 * read as a circuit rather than as a default node graph.
 */

/** Fixed columns and rhythm, in user units (1 unit = 1 px at 100 % width). */
export const DIAGRAM = {
  width: 1000,
  padTop: 46,
  padBottom: 24,
  /** The instance box. `x1` is THE LINE — what a real call has to cross. */
  boundaryX0: 12,
  boundaryX1: 578,
  coreX: 30,
  coreW: 140,
  coreH: 92,
  /** The internal bus every branch leaves from. */
  busX: 196,
  laneLabelX: 212,
  /** In-process providers: right edge lands 16 px short of the boundary. */
  insideX: 356,
  /** Real vendors, beyond the boundary. */
  outsideX: 632,
  nodeW: 206,
  nodeH: 32,
  ghostX: 862,
  ghostW: 128,
  ghostH: 24,
  ghostGap: 6,
  rowH: 42,
  laneHeaderH: 22,
  laneGap: 12,
  /** Where the arrowhead's base sits, relative to the node's left edge. */
  headLength: 9,
} as const;

/** Pips travel at a constant speed, so a longer reach visibly takes longer. */
const PIP_SPEED_PX_PER_SEC = 96;
export const PIPS_PER_LINK = 3;

/** What a node IS. `ghost` and `unknown` are not modes — see `types.ts`. */
export type DiagramTone = IntegrationMode | 'ghost' | 'unknown';

export type DiagramNodeKind = 'wired' | 'ghost' | 'unknown' | 'nothing';

export interface DiagramNode {
  id: string;
  kind: DiagramNodeKind;
  tone: DiagramTone;
  /** Already resolved through `vendorLabel()` / the caller's label table. */
  label: string;
  /** The raw wire token, for the `<title>` tooltip. Absent on `nothing`. */
  vendor?: string;
  declaredBy?: string;
  category?: string;
  /** INSIDE the boundary — the geometric claim this whole view exists to make. */
  inside: boolean;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DiagramLink {
  id: string;
  /** The node this link feeds. */
  nodeId: string;
  tone: DiagramTone;
  /** The path the arrow draws AND the pips travel — one string, so they agree. */
  d: string;
  /** Arrowhead, drawn as a triangle rather than a marker so it inherits the tone. */
  head: string;
  /** Seconds for one pip to travel `d`, so speed is constant across lengths. */
  durationSec: number;
  /** Does traffic leave the instance? Exactly `!node.inside` for a wired link. */
  crosses: boolean;
  /** Where it crosses — the gate drawn on the boundary edge. */
  crossing?: { x: number; y: number };
  /** An unreachable declarant: the link is drawn with a gap and a warning. */
  broken?: { gapX: number; y: number };
}

export interface DiagramLane {
  id: string;
  kind: 'category' | 'unknown';
  /** Resolved category name, or the unknown lane's caption. */
  label: string;
  y: number;
  h: number;
  /** Baseline of the lane caption. */
  labelY: number;
}

export interface DiagramCounts {
  categories: number;
  wired: number;
  mock: number;
  ghost: number;
  unknown: number;
}

export interface Diagram {
  width: number;
  height: number;
  boundary: { x: number; y: number; w: number; h: number };
  /** The right edge of the boundary — the only line that means anything. */
  boundaryX: number;
  core: { x: number; y: number; w: number; h: number };
  bus: { x: number; y0: number; y1: number };
  lanes: DiagramLane[];
  nodes: DiagramNode[];
  links: DiagramLink[];
  counts: DiagramCounts;
}

/** Every word the diagram renders arrives here; this module holds no copy. */
export interface DiagramLabels {
  /** `vendorLabel(t, category, vendor)`, bound by the caller. */
  vendor: (category: string, vendor: string) => string;
  category: (category: string) => string;
  /** Caption of the lane holding declarants we could not reach. */
  unknownLane: string;
  /** Shown in place of a node when a category has nothing wired. */
  nothingWired: string;
}

/**
 * Inside or outside — the single place the rule lives, so the picture and the
 * list can never disagree about what is simulated.
 */
export function nodePlacement(descriptor: IntegrationDescriptor): {
  inside: boolean;
  tone: DiagramTone;
  kind: DiagramNodeKind;
} {
  if (!descriptor.enabled) return { inside: false, tone: 'ghost', kind: 'ghost' };
  // M2: the mode in use now, not the boot mode.
  const mode = effectiveMode(descriptor);
  return { inside: mode === 'mock', tone: mode, kind: 'wired' };
}

/** SVG has no ellipsis. Approximate at 0.56 em/char, which over-estimates a
 *  little — a label is clipped a hair early rather than overflowing its box. */
export function fitText(text: string, maxWidth: number, fontSize: number): string {
  const max = Math.max(1, Math.floor(maxWidth / (fontSize * 0.56)));
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(1, max - 1))}…`;
}

function link(
  id: string,
  nodeId: string,
  tone: DiagramTone,
  rowY: number,
  nodeX: number,
  crosses: boolean,
): DiagramLink {
  const from = DIAGRAM.busX;
  const to = nodeX - DIAGRAM.headLength;
  return {
    id,
    nodeId,
    tone,
    d: `M${from},${rowY} L${to},${rowY}`,
    head: `M${to},${rowY - 4.5} L${nodeX - 1},${rowY} L${to},${rowY + 4.5} Z`,
    durationSec: Math.max(1.6, Math.round(((to - from) / PIP_SPEED_PX_PER_SEC) * 10) / 10),
    crosses,
    ...(crosses ? { crossing: { x: DIAGRAM.boundaryX1, y: rowY } } : {}),
  };
}

/**
 * The whole picture, from the inventory the section already read. Pure: same
 * payload in, same coordinates out — which is what makes it testable without a
 * DOM and what keeps the component free of arithmetic.
 */
export function buildDiagram(inventory: ProvidersIntegrations, labels: DiagramLabels): Diagram {
  const D = DIAGRAM;
  const groups = groupByCategory(inventory.integrations);
  const unknown = unknownEntries(inventory);

  const lanes: DiagramLane[] = [];
  const nodes: DiagramNode[] = [];
  const links: DiagramLink[] = [];

  let y = D.padTop;

  const pushLane = (
    id: string,
    kind: DiagramLane['kind'],
    label: string,
    contentH: number,
  ): number => {
    const h = D.laneHeaderH + contentH;
    lanes.push({ id, kind, label, y, h, labelY: y + 14 });
    const contentTop = y + D.laneHeaderH;
    y += h + D.laneGap;
    return contentTop;
  };

  // The unreachable declarants come FIRST, exactly as the list orders them: a
  // service we could not ask is the only thing on this page that may be an
  // outage, and we do not know which category it belongs to.
  if (unknown.length > 0) {
    const contentH = Math.max(unknown.length * D.rowH, D.rowH);
    const top = pushLane('unknown', 'unknown', labels.unknownLane, contentH);
    unknown.forEach((entry, i) => {
      const rowY = top + i * D.rowH + D.rowH / 2;
      const id = `unknown-${entry.declaredBy}`;
      nodes.push({
        id,
        kind: 'unknown',
        tone: 'unknown',
        label: entry.declaredBy,
        vendor: entry.declaredBy,
        declaredBy: entry.declaredBy,
        inside: true,
        x: D.insideX,
        y: rowY - D.nodeH / 2,
        w: D.nodeW,
        h: D.nodeH,
      });
      const broken = link(`link-${id}`, id, 'unknown', rowY, D.insideX, false);
      links.push({
        ...broken,
        broken: { gapX: (D.busX + D.insideX - D.headLength) / 2, y: rowY },
      });
    });
  }

  for (const group of groups) {
    const wiredH = Math.max(group.wired.length, 1) * D.rowH;
    const ghostH =
      group.unwired.length > 0 ? group.unwired.length * (D.ghostH + D.ghostGap) - D.ghostGap : 0;
    const contentH = Math.max(wiredH, ghostH, D.rowH);
    const top = pushLane(
      `category-${group.category}`,
      'category',
      labels.category(group.category),
      contentH,
    );

    // Wired rows, vertically centred in the lane so a tall ghost column does
    // not push the real traffic to the top of an empty band.
    const wiredTop = top + (contentH - wiredH) / 2;
    if (group.wired.length === 0) {
      nodes.push({
        id: `nothing-${group.category}`,
        kind: 'nothing',
        tone: 'ghost',
        label: labels.nothingWired,
        category: group.category,
        inside: true,
        x: D.insideX,
        y: wiredTop + D.rowH / 2 - D.nodeH / 2,
        w: D.nodeW,
        h: D.nodeH,
      });
    } else {
      group.wired.forEach((descriptor, i) => {
        const rowY = wiredTop + i * D.rowH + D.rowH / 2;
        const { inside, tone, kind } = nodePlacement(descriptor);
        const id = integrationKey(descriptor);
        const x = inside ? D.insideX : D.outsideX;
        nodes.push({
          id,
          kind,
          tone,
          label: labels.vendor(descriptor.category, descriptor.vendor),
          vendor: descriptor.vendor,
          declaredBy: descriptor.declaredBy,
          category: descriptor.category,
          inside,
          x,
          y: rowY - D.nodeH / 2,
          w: D.nodeW,
          h: D.nodeH,
        });
        links.push(link(`link-${id}`, id, tone, rowY, x, !inside));
      });
    }

    // Ghosts: outside, in their own column, and deliberately unconnected.
    const ghostTop = top + (contentH - ghostH) / 2;
    group.unwired.forEach((descriptor, i) => {
      nodes.push({
        id: integrationKey(descriptor),
        kind: 'ghost',
        tone: 'ghost',
        label: labels.vendor(descriptor.category, descriptor.vendor),
        vendor: descriptor.vendor,
        declaredBy: descriptor.declaredBy,
        category: descriptor.category,
        inside: false,
        x: D.ghostX,
        y: ghostTop + i * (D.ghostH + D.ghostGap),
        w: D.ghostW,
        h: D.ghostH,
      });
    });
  }

  const height = Math.round(Math.max(y - D.laneGap, D.padTop) + D.padBottom);

  // The bus spans the rows it actually feeds — not the whole canvas, so it does
  // not trail past the last branch.
  const rowYs = links.map((l) => l.crossing?.y ?? nodeCentreY(nodes, l.nodeId));
  const busY0 = rowYs.length ? Math.min(...rowYs) : height / 2;
  const busY1 = rowYs.length ? Math.max(...rowYs) : height / 2;

  const wired = inventory.integrations.filter((i) => i.enabled);

  return {
    width: D.width,
    height,
    boundary: { x: D.boundaryX0, y: 10, w: D.boundaryX1 - D.boundaryX0, h: height - 20 },
    boundaryX: D.boundaryX1,
    core: {
      x: D.coreX,
      y: Math.round(
        Math.min(Math.max((busY0 + busY1) / 2 - D.coreH / 2, 22), height - D.coreH - 22),
      ),
      w: D.coreW,
      h: D.coreH,
    },
    bus: { x: D.busX, y0: busY0, y1: busY1 },
    lanes,
    nodes,
    links,
    counts: {
      categories: groups.length,
      wired: wired.length,
      mock: wired.filter((i) => effectiveMode(i) === 'mock').length,
      ghost: inventory.integrations.length - wired.length,
      unknown: unknown.length,
    },
  };
}

function nodeCentreY(nodes: DiagramNode[], nodeId: string): number {
  const node = nodes.find((n) => n.id === nodeId);
  return node ? node.y + node.h / 2 : 0;
}
